import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import campaignService from '../../services/campaign.service';
import { useEyeconxStore } from '../../store/eyeconxStore';

function TemplateSelectionStep({
    templateOption,
    setTemplateOption,
    templates,
    setTemplates,
    selectedTemplate,
    setSelectedTemplate,
    loadingTemplates,
    setLoadingTemplates,
    templateType = 'EMAIL',
    onTemplateApplied,
}) {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectingId, setSelectingId] = useState(null);

    const loadTemplates = useCallback(() => {
        if (!clinicId) {
            setTemplates([]);
            return;
        }

        setLoadingTemplates(true);
        campaignService
            .getTemplates(clinicId, 0, 50, {
                typeFilter: templateType,
                activeFilter: 'true',
                searchTerm,
                sortBy: 'createdAt',
                sortOrder: 'DESC',
            })
            .then((res) => {
                setTemplates(res.data?.content || []);
            })
            .catch(() => {
                setTemplates([]);
                toast.error('Failed to load campaign templates.');
            })
            .finally(() => setLoadingTemplates(false));
    }, [clinicId, searchTerm, setLoadingTemplates, setTemplates, templateType]);

    useEffect(() => {
        if (templateOption !== 'template') {
            return;
        }
        loadTemplates();
    }, [templateOption, loadTemplates]);

    const handleChooseBlank = () => {
        setTemplateOption('blank');
        setSelectedTemplate(null);
        onTemplateApplied?.(null);
    };

    const handleChooseTemplateMode = () => {
        setTemplateOption('template');
    };

    const handleSelectTemplate = async (template) => {
        if (!clinicId || !template?.id) {
            return;
        }

        setSelectingId(template.id);
        try {
            // Fetch full template so jsonContent/HTML are complete for the editor.
            const res = await campaignService.getTemplateById(clinicId, template.id);
            const fullTemplate = res.data || template;
            setSelectedTemplate(fullTemplate);
            onTemplateApplied?.(fullTemplate);
            toast.success(`Template "${fullTemplate.name || 'Untitled'}" selected.`);
        } catch (error) {
            console.error('Failed to load campaign template', error);
            toast.error('Failed to load the selected template.');
        } finally {
            setSelectingId(null);
        }
    };

    return (
        <div className="animate-fade-in">
            <h3 className="fw-normal text-gray-900 mb-2">Select Template</h3>
            <p className="text-muted mb-4">
                Start from scratch or use an active campaign template from 3ConX.
            </p>

            <div className="row g-4 mb-4">
                <div className="col-md-6">
                    <button
                        type="button"
                        className={`card h-100 w-100 text-start border-2 ${
                            templateOption === 'blank'
                                ? 'border-primary bg-light'
                                : 'border-light-subtle'
                        }`}
                        style={{ cursor: 'pointer' }}
                        onClick={handleChooseBlank}
                    >
                        <div className="card-body p-4 text-center">
                            <i className="bi bi-plus-circle display-6 text-primary mb-3 d-block" />
                            <h5 className="fw-normal mb-2">Start Blank</h5>
                            <p className="text-muted mb-0">Create a new blast from scratch</p>
                        </div>
                    </button>
                </div>
                <div className="col-md-6">
                    <button
                        type="button"
                        className={`card h-100 w-100 text-start border-2 ${
                            templateOption === 'template'
                                ? 'border-primary bg-light'
                                : 'border-light-subtle'
                        }`}
                        style={{ cursor: 'pointer' }}
                        onClick={handleChooseTemplateMode}
                    >
                        <div className="card-body p-4 text-center">
                            <i className="bi bi-layout-text-window-reverse display-6 text-primary mb-3 d-block" />
                            <h5 className="fw-normal mb-2">Use Template</h5>
                            <p className="text-muted mb-0">Select from active campaign templates</p>
                        </div>
                    </button>
                </div>
            </div>

            {templateOption === 'template' && (
                <div className="card border-0 bg-light">
                    <div className="card-body">
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                            <h5 className="fw-normal mb-0">Available Templates</h5>
                            <form
                                className="input-group input-group-sm"
                                style={{ maxWidth: 280 }}
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    loadTemplates();
                                }}
                            >
                                <input
                                    type="search"
                                    className="form-control"
                                    placeholder="Search templates..."
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                />
                                <button type="submit" className="btn btn-outline-primary" title="Search">
                                    <i className="bi bi-search" />
                                </button>
                            </form>
                        </div>

                        {loadingTemplates ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : templates.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                <i className="bi bi-inbox display-6 d-block mb-2" />
                                <p className="mb-0">No active {templateType.toLowerCase()} templates available.</p>
                            </div>
                        ) : (
                            <div className="row g-3">
                                {templates.map((template) => {
                                    const isSelected = selectedTemplate?.id === template.id;
                                    const isSelecting = selectingId === template.id;
                                    return (
                                        <div className="col-sm-6 col-lg-4" key={template.id}>
                                            <div
                                                className={`card h-100 ${
                                                    isSelected ? 'border-primary shadow-sm' : 'border'
                                                }`}
                                            >
                                                <div
                                                    className="bg-white border-bottom d-flex align-items-center justify-content-center"
                                                    style={{ height: 140 }}
                                                >
                                                    {template.preview ? (
                                                        <img
                                                            src={template.preview}
                                                            alt={template.name || 'Template preview'}
                                                            style={{
                                                                maxWidth: '100%',
                                                                maxHeight: '100%',
                                                                objectFit: 'contain',
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="text-muted small text-center px-3">
                                                            <i className="bi bi-image d-block mb-1 fs-4" />
                                                            No preview image
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="card-body d-flex flex-column">
                                                    <h6 className="fw-normal mb-1 text-truncate" title={template.name}>
                                                        {template.name || 'Untitled'}
                                                    </h6>
                                                    <div className="text-muted small mb-3">
                                                        {template.createdAt || '—'}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className={`btn btn-sm mt-auto ${
                                                            isSelected ? 'btn-primary' : 'btn-outline-primary'
                                                        }`}
                                                        disabled={isSelecting}
                                                        onClick={() => handleSelectTemplate(template)}
                                                    >
                                                        {isSelecting ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-1" />
                                                                Loading...
                                                            </>
                                                        ) : isSelected ? (
                                                            <>
                                                                <i className="bi bi-check-lg me-1" />
                                                                Selected
                                                            </>
                                                        ) : (
                                                            'Select'
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default TemplateSelectionStep;
